import { useCurrentTenant, useMyTenants } from '@/lib/api/tenants';
import { getActiveTenantId } from '@/lib/tenant';
import { useUsage } from '@/lib/api/usage';
import { useBalance, useBillingPortal, useCatalog, useCheckout } from '@/lib/api/billing';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { QueryState } from '@/components/QueryState';
import { Skeleton, SkeletonRows } from '@/components/ui/Skeleton';
import { useWorkspacePath } from '@/lib/workspacePath';
import type { BalanceStatus, CatalogEntry } from '@/lib/api/types';

function formatCurrency(value: number | undefined): string {
  if (value === undefined) return '-';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);
}

function statusBanner(status: BalanceStatus): { tone: 'warn' | 'block'; text: string } | null {
  switch (status) {
    case 'warning':
      return {
        tone: 'warn',
        text: 'You have used most of your token allowance. Buy a credit pack or upgrade your plan to avoid interruption.',
      };
    case 'chat_blocked':
      return {
        tone: 'block',
        text: 'Your token balance is exhausted and chat is paused. Buy a credit pack or upgrade your plan to continue.',
      };
    case 'hard_blocked':
      return {
        tone: 'block',
        text: 'Your token balance is exhausted and all channels are paused. Buy a credit pack or upgrade your plan to continue.',
      };
    default:
      return null;
  }
}

export function BillingPage() {
  const tenant = useCurrentTenant();
  const myTenants = useMyTenants();
  const balance = useBalance();
  const catalog = useCatalog();
  const usage = useUsage();
  const checkout = useCheckout();
  const portal = useBillingPortal();
  const ws = useWorkspacePath();

  const role = myTenants.data?.find((m) => m.tenant.id === getActiveTenantId())?.role;
  const canManage = role === 'owner' || role === 'admin';

  // Stripe returns the user here after checkout/portal; the URL must carry the
  // workspace slug prefix (spec 0013) or it lands on workspace-not-available.
  const billingUrl = `${window.location.origin}${ws('settings', 'billing')}`;

  function handleCheckout(planKey: string) {
    checkout.mutate(
      {
        plan_key: planKey,
        success_url: `${billingUrl}?status=success`,
        cancel_url: `${billingUrl}?status=cancel`,
      },
      { onSuccess: (res) => window.location.assign(res.url) },
    );
  }

  function handlePortal() {
    portal.mutate(
      { return_url: billingUrl },
      { onSuccess: (res) => window.location.assign(res.url) },
    );
  }

  const banner = balance.data ? statusBanner(balance.data.status) : null;
  const percent = balance.data ? Math.min(100, Math.round(balance.data.percent_used * 100)) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Billing</h1>
        <p className="mt-1 text-sm text-gray-500">
          Plan, token balance, and usage for this workspace.
        </p>
      </div>

      {banner && (
        <div
          role="alert"
          data-testid="billing-banner"
          className={
            banner.tone === 'block'
              ? 'rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'
              : 'rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'
          }
        >
          {banner.text}
        </div>
      )}

      <Card title="Plan and balance">
        <QueryState
          isLoading={balance.isLoading}
          error={balance.error}
          loadingLabel="Loading balance"
          skeleton={<Skeleton className="h-24 w-full" />}
        >
          {balance.data && (
            <div className="space-y-4" data-testid="billing-balance">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <p className="text-2xl font-semibold text-gray-900">{balance.data.plan_name}</p>
                  <p className="mt-1 text-sm text-gray-500">
                    {balance.data.used_weighted_tokens.toLocaleString()} of{' '}
                    {balance.data.monthly_allowance.toLocaleString()} weighted tokens used this
                    month
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  Credits remaining:{' '}
                  <span className="font-semibold text-gray-900">
                    {balance.data.credits_remaining.toLocaleString()}
                  </span>
                </p>
              </div>
              <div
                className="h-2 w-full overflow-hidden rounded-full bg-gray-100"
                role="progressbar"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className={
                    balance.data.status === 'ok'
                      ? 'h-full bg-brand-600'
                      : balance.data.status === 'warning'
                        ? 'h-full bg-amber-500'
                        : 'h-full bg-red-600'
                  }
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          )}
        </QueryState>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Plans">
          <QueryState
            isLoading={catalog.isLoading}
            error={catalog.error}
            loadingLabel="Loading plans"
            skeleton={<SkeletonRows rows={4} />}
          >
            <div className="space-y-3" data-testid="billing-packages">
              {catalog.data?.packages.map((pkg) => (
                <PlanRow
                  key={pkg.key}
                  entry={pkg}
                  current={balance.data?.plan_key === pkg.key}
                  canManage={canManage}
                  pending={checkout.isPending}
                  actionLabel="Switch"
                  onSelect={() => handleCheckout(pkg.key)}
                />
              ))}
            </div>
          </QueryState>
        </Card>

        <Card
          title="Credit packs"
          description="One-time top-ups; credits never expire and are spent after your monthly allowance."
        >
          <QueryState
            isLoading={catalog.isLoading}
            error={catalog.error}
            loadingLabel="Loading credit packs"
            skeleton={<SkeletonRows rows={2} />}
          >
            <div className="space-y-3" data-testid="billing-credit-packs">
              {catalog.data?.credit_packs.map((pack) => (
                <PlanRow
                  key={pack.key}
                  entry={pack}
                  current={false}
                  canManage={canManage}
                  pending={checkout.isPending}
                  actionLabel="Buy"
                  onSelect={() => handleCheckout(pack.key)}
                />
              ))}
            </div>
          </QueryState>
          <div className="mt-4">
            <Button
              variant="secondary"
              onClick={handlePortal}
              disabled={!canManage || portal.isPending}
              className="w-full"
              data-testid="billing-portal"
            >
              {portal.isPending ? 'Opening...' : 'Manage billing'}
            </Button>
          </div>
        </Card>
      </div>

      {!canManage && (
        <p className="text-sm text-gray-500">
          Only workspace owners and admins can change the plan or buy credits.
        </p>
      )}
      {(checkout.error || portal.error) && (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {((checkout.error ?? portal.error) as Error).message}
        </p>
      )}

      <Card
        title="Token usage"
        description={usage.data?.period ?? 'Current period'}
      >
        <QueryState
          isLoading={usage.isLoading}
          error={usage.error}
          loadingLabel="Loading usage"
          skeleton={<SkeletonRows rows={4} />}
        >
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat label="Weighted tokens" value={usage.data?.weighted_tokens.toLocaleString()} />
            <Stat label="Total tokens" value={usage.data?.total_tokens.toLocaleString()} />
            <Stat label="Estimated cost" value={formatCurrency(usage.data?.estimated_cost_usd)} />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-600">
                  <th className="py-2 pr-4 font-medium">Model</th>
                  <th className="py-2 pr-4 font-medium">Weighted</th>
                  <th className="py-2 pr-4 font-medium">Total</th>
                  <th className="py-2 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {usage.data?.by_model?.map((row) => (
                  <tr key={row.model}>
                    <td className="py-2 pr-4 text-gray-900">{row.model}</td>
                    <td className="py-2 pr-4 text-gray-600">
                      {row.weighted_tokens.toLocaleString()}
                    </td>
                    <td className="py-2 pr-4 text-gray-600">
                      {(row.input_tokens + row.output_tokens).toLocaleString()}
                    </td>
                    <td className="py-2 text-gray-600">{formatCurrency(row.estimated_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </Card>

      {tenant.isLoading && (
        <span role="status" className="sr-only">
          Loading workspace
        </span>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-600">{label}</p>
      <p className="text-lg font-semibold text-gray-900">{value ?? '-'}</p>
    </div>
  );
}

function PlanRow({
  entry,
  current,
  canManage,
  pending,
  actionLabel,
  onSelect,
}: {
  entry: CatalogEntry;
  current: boolean;
  canManage: boolean;
  pending: boolean;
  actionLabel: string;
  onSelect: () => void;
}) {
  return (
    <div
      className={
        current
          ? 'flex items-center justify-between rounded-md border border-brand-300 bg-brand-50 px-4 py-3'
          : 'flex items-center justify-between rounded-md border border-gray-200 px-4 py-3'
      }
      data-testid={`plan-${entry.key}`}
    >
      <div>
        <p className="font-medium text-gray-900">{entry.name}</p>
        <p className="text-sm text-gray-500">
          {entry.included_tokens.toLocaleString()} weighted tokens
        </p>
      </div>
      {current ? (
        <span className="rounded-full bg-brand-600 px-3 py-1 text-xs font-medium text-white">
          Current plan
        </span>
      ) : (
        <Button
          variant="primary"
          onClick={onSelect}
          disabled={!entry.purchasable || !canManage || pending}
          data-testid={`checkout-${entry.key}`}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
